<script setup lang="ts">
/**
 * Partner-portal shell — PortalHeader + main slot + AccountPanel.
 *
 * Background is gray-10 via the `.fz-portal-shell` class in
 * `design-system/shell.css`, scoped so the existing auth / account
 * routes are untouched.
 *
 * Phase 2 wiring:
 *   - `onMounted` triggers `portalSelf.fetchOnce()` so all four
 *     consumers (Home, Coupons, Payments, AccountPanel) read from
 *     a single hydrated snapshot rather than refetching per route.
 *   - `<AccountPanel>` is mounted here so it's available on every
 *     portal page; the `accountPanel` Pinia store drives its
 *     `isOpen` and `view`.
 *   - PortalHeader's user-menu emit (`select-menu-item`) routes
 *     `main`/`iban`/`email` into the panel store and re-emits
 *     `logout` upward for App.vue to handle (auth-store call +
 *     login redirect).
 *
 * The shell also hosts the PWA install-prompt notification —
 * portal-only because partners install on phones; PMS users sit at
 * desktops with multiple monitors.
 */
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import CdsInlineNotification from '../atoms/CdsInlineNotification.vue'
import CdsButton from '../atoms/CdsButton.vue'
import AccountPanel from './AccountPanel.vue'
import PortalHeader, { type PortalHeaderTab, type PortalMenuItem } from './PortalHeader.vue'
import { useInstallStore } from '@/stores/install.store'
import { useAccountPanelStore } from '@/stores/accountPanel.store'
import { usePortalSelfStore } from '@/stores/portalSelf.store'
import type { PortalSelfResponse } from '@/types/portal'

defineProps<{
  tabs: PortalHeaderTab[]
  activeId?: string
  partnerName?: string
  partnerSubtitle?: string
  avatarInitials?: string
}>()

const emit = defineEmits<{
  selectTab: [id: string]
  logout: []
}>()

const { t } = useI18n({ useScope: 'global' })
const installStore = useInstallStore()
const { deferredPrompt, dismissed } = storeToRefs(installStore)

const accountPanel = useAccountPanelStore()
const { isOpen: panelOpen, view: panelView } = storeToRefs(accountPanel)

const portalSelf = usePortalSelfStore()
const { partner } = storeToRefs(portalSelf)

const showInstallPrompt = computed(() => deferredPrompt.value !== null && !dismissed.value)

onMounted(() => {
  // Fire-and-forget; the store handles in-flight + populated guards.
  void portalSelf.fetchOnce()
})

async function onInstall(): Promise<void> {
  await installStore.promptInstall()
}

function onDismiss(): void {
  installStore.dismiss()
}

function onSelectMenuItem(item: PortalMenuItem): void {
  if (item === 'logout') {
    emit('logout')
    return
  }
  accountPanel.open(item)
}

function onPanelPartnerUpdate(p: PortalSelfResponse): void {
  portalSelf.setPartner(p)
}
</script>

<template>
  <div class="fz-portal-shell">
    <PortalHeader
      :tabs="tabs"
      :active-id="activeId"
      :partner-name="partnerName"
      :partner-subtitle="partnerSubtitle"
      :partner-email="partnerSubtitle"
      :avatar-initials="avatarInitials"
      @select-tab="(id) => emit('selectTab', id)"
      @select-menu-item="onSelectMenuItem"
    />
    <CdsInlineNotification
      v-if="showInstallPrompt"
      class="fz-portal-shell__install"
      variant="info"
      :title="t('install.title')"
      :subtitle="t('install.body')"
      :close-label="t('install.dismiss')"
      @close="onDismiss"
    >
      <div class="fz-portal-shell__install-actions">
        <CdsButton variant="primary" size="sm" @click="onInstall">
          {{ t('install.install') }}
        </CdsButton>
        <CdsButton variant="ghost" size="sm" @click="onDismiss">
          {{ t('install.dismiss') }}
        </CdsButton>
      </div>
    </CdsInlineNotification>
    <main>
      <slot />
    </main>
    <AccountPanel
      :is-open="panelOpen"
      :view="panelView"
      :partner="partner"
      @close="accountPanel.close"
      @update:view="(v) => accountPanel.setView(v)"
      @partner-update="onPanelPartnerUpdate"
      @logout="emit('logout')"
    />
  </div>
</template>

<style scoped>
/* Layout-only. Visual rules are on .fz-portal-shell in design-system/shell.css. */
main {
  display: block;
}
.fz-portal-shell__install {
  margin: var(--spacing-04) var(--spacing-05) 0;
}
.fz-portal-shell__install-actions {
  display: flex;
  gap: var(--spacing-04);
  margin-top: var(--spacing-03);
}
</style>
